import React, { useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import withRouter from "components/Common/withRouter";
import { DEFAULT_PAGE_SIZE, pascalToCamel } from "helpers/GeneralHelper";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import TableContainer from "components/Common/TableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import Spinners from "components/Common/Spinner";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { useOffboardingContext } from "libs/context/OffboardingContext";
import { deleteOffboarding, submitOffboardingFilterData } from "model/OffboardingModel";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const OffBoardingList = () => {
    const { permissionData } = usePermissionContext() || {};
    const { fetchOffboardingContextData, offboardingContextData } = useOffboardingContext() || {};
    const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("Offboarding");
    const initialFilterFetchedRef = useRef(false);
    const [modal1, setModal1] = useState(false);
    const toggleViewModal = () => setModal1(!modal1);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterForm, setFilterForm] = useState({
      search: "",
      fromDate: "",
      toDate: "",
    });
    const [offboardingData, setOffboardingData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableMetaData, setTableMetaData] = useState([]);
    const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
    const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
    const [paginationMeta, setPaginationMeta] = useState({
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      totalPages: 0,
      totalRecords: 0,
    });

    const filteredData = useMemo(() => offboardingData, [offboardingData]);

    const offboardingActionPermissions = useMemo(
        () => ({
            canDelete: Boolean(
                permissionData?.OffboardingAddAccess || permissionData?.OffboardingAccess
            ),
        }),
        [permissionData]
    );

    const hasAnyOffboardingRowAction = useMemo(
        () => offboardingActionPermissions.canDelete,
        [offboardingActionPermissions]
    );

    useEffect(() => {
      fetchOffboardingContextData?.();
    }, []);

    useEffect(() => {
      if (!settingsReady || initialFilterFetchedRef.current) return;
      initialFilterFetchedRef.current = true;
      const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
      setRequestPayload((previous) => ({ ...previous, pageSize }));
      setPaginationMeta((previous) => ({ ...previous, pageSize }));
      getOffboardingList({ pageNumber: 1, pageSize });
    }, [settingsReady, settingsPageSize]);

    useEffect(() => {
      const valueReModified = offboardingContextData?.table?.columns?.map((col) => ({
        Header: col?.title,
        accessor: pascalToCamel(col?.field),
        type: col?.type,
      }));
      setTableMetaData(valueReModified || []);
    }, [offboardingContextData]);

    const getOffboardingList = async (payloadOverrides = {}) => {
      setLoading(true);
      const payload = {
        ...requestPayload,
        ...payloadOverrides,
      };
      const response = await submitOffboardingFilterData(payload);
      const responseData = response?.data || {};
      const listData = responseData?.records || [];
      const valueReModified = listData?.map((item = {}) => ({
        ...item,
        id: item?.id || item?._id || "",
        company: item?.company || item?.companyName || "",
        client: item?.client || item?.clientName || "",
        email: item?.email || "",
        regDate: item?.regDate || item?.reg_date || item?.createdOn || "",
        lworkDate: item?.lworkDate || item?.lwork_date || "",
        status: item?.status || "",
        canDelete: String(item?.status ?? "").toLowerCase() === "pending",
      }));

      if (response) {
        setOffboardingData(valueReModified || []);
        setPaginationMeta({
          pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
          pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
          totalPages: Number(responseData?.totalPages) || 0,
          totalRecords: Number(responseData?.totalRecords) || 0,
        });
        setRequestPayload(payload);
      } else {
        setOffboardingData([]);
      }
      setLoading(false);
    };

    const handleFilterSubmit = async (data) => {
      await getOffboardingList({
        ...data,
        search: requestPayload?.search,
        pageNumber: 1,
      });
      setCommonFilterModalOpen(false);
    };

    const handleSearchSubmit = async (value, extraFilters = {}) => {
      const trimmedValue = value?.trim() || "";
      const nextPayload = {
        ...requestPayload,
        pageNumber: 1,
        search: trimmedValue,
        name: trimmedValue,
        fromDate: extraFilters?.fromDate || null,
        toDate: extraFilters?.toDate || null,
      };
      if (!trimmedValue) {
        delete nextPayload.search;
        delete nextPayload.name;
      }
      await getOffboardingList(nextPayload);
    };

    const handleFilterFieldChange = (e) => {
      const { name, value } = e.target;
      setFilterForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    const handlePageChange = async (page) => {
      await getOffboardingList({ pageNumber: page });
    };

    const handleRowsPerPageChange = async (rows) => {
      await getOffboardingList({ pageNumber: 1, pageSize: rows });
    };

    const handleDelete = async (row = {}) => {
      if (!row?.canDelete) return;

      const id = String(row?.id ?? "").trim();
      if (!id) {
        toast.error("Record ID is missing");
        return;
      }

      const swalResult = await Swal.fire({
        title: "Delete offboarding record?",
        text: "Are you sure you want to delete this record?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "OK",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      });
      if (!swalResult?.isConfirmed) return;

      setLoading(true);
      try {
        const res = await deleteOffboarding(id);
        if (res?.success !== false && (res?.success || res?.isSuccess || res?.data != null)) {
          toast.success(res?.message || "Deleted successfully");
          await getOffboardingList();
          return;
        }
        toast.error(res?.message || "Unable to delete");
      } finally {
        setLoading(false);
      }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    {/* View Modal */}
                    <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Offboarding List</h5>
                            <hr />

                            <LegacyFilterToolbar>
                            <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                              <Col xs="12" md="4" className="metadata-filter-field metadata-filter-field--text">
                                <span className="manage-user-filter-label">Search</span>
                                <div className="metadata-filter-field__control">
                                <Input
                                  type="text"
                                  name="search"
                                  placeholder="Search by Email/Company etc."
                                  className={LEGACY_FILTER_INPUT_CLASS}
                                  value={filterForm.search}
                                  onChange={handleFilterFieldChange}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      setSearchQuery(filterForm.search);
                                      handleSearchSubmit(filterForm.search, filterForm);
                                    }
                                  }}
                                />
                                </div>
                              </Col>
                              <Col xs="12" md="3" className="metadata-filter-field metadata-filter-field--date">
                                <span className="manage-user-filter-label">From date</span>
                                <div className="metadata-filter-field__control">
                                <Input
                                  type="date"
                                  name="fromDate"
                                  value={filterForm.fromDate}
                                  onChange={handleFilterFieldChange}
                                  className={LEGACY_FILTER_INPUT_CLASS}
                                />
                                </div>
                              </Col>
                              <Col xs="12" md="3" className="metadata-filter-field metadata-filter-field--date">
                                <span className="manage-user-filter-label">To date</span>
                                <div className="metadata-filter-field__control">
                                <Input
                                  type="date"
                                  name="toDate"
                                  value={filterForm.toDate}
                                  onChange={handleFilterFieldChange}
                                  className={LEGACY_FILTER_INPUT_CLASS}
                                />
                                </div>
                              </Col>
                              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--actions">
                                <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                                <div className="metadata-filter-toolbar__actions">
                                <Button
                                  color="primary"
                                  className="w-100 manage-user-search-btn"
                                  onClick={() => {
                                    setSearchQuery(filterForm.search);
                                    handleSearchSubmit(filterForm.search, filterForm);
                                  }}
                                >
                                  <i className="fa fa-search me-1"></i>
                                  Search
                                </Button>
                                </div>
                              </Col>
                            </Row>
                            </LegacyFilterToolbar>

                            {!loading && offboardingData?.length && tableMetaData?.length ? (
                              <TableContainer
                                columns={tableMetaData}
                                data={filteredData}
                                isActionRequired={hasAnyOffboardingRowAction}
                                isCheckboxLeftSide={false}
                                isPagination={true}
                                currentPage={paginationMeta?.pageNumber}
                                rowsPerPage={paginationMeta?.pageSize}
                                totalPages={paginationMeta?.totalPages}
                                totalRecords={paginationMeta?.totalRecords}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handleRowsPerPageChange}
                                setCommonFilterModalOpen={setCommonFilterModalOpen}
                                onDelete={
                                    offboardingActionPermissions.canDelete
                                        ? handleDelete
                                        : undefined
                                }
                                pageName={"Offboarding"}
                              />
                            ) : (
                              // <Card className="border mb-3">
                              //   <CardBody>
                              //     <Spinners setLoading={setLoading} />
                              //   </CardBody>
                              // </Card>
                              <Loaders />

                            )}
                        </CardBody>
                    </Card>
                    <AllCommonFilterModal
                      setCommonFilterModalOpen={setCommonFilterModalOpen}
                      commonFilterModalOpen={commonFilterModalOpen}
                      modalTitle="Offboarding"
                      handleSubmit={handleFilterSubmit}
                      filterData={offboardingContextData?.filters || []}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

OffBoardingList.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(OffBoardingList);
